/**
 * The one file that imports `posthog-js`. Analytics starts for every visitor
 * without asking (owner, 2026-10-07: the consent banner is gone; Privacy
 * Policy section 6), except where `optedOut()` says no. Every call here is a
 * no-op until the SDK is initialized, so call sites never need to check
 * anything themselves — `track`/`identify`/`pageview` are always safe to call.
 *
 * The SDK is a chunk of its own, fetched once the first screen has drawn
 * (faster-screens 03): it is a third of the entry chunk's weight otherwise.
 * A call made while it loads waits in `pending` and is sent on load, in
 * order, stamped with the time it was made.
 */
import type { PostHog } from 'posthog-js';
import { whenIdle } from './idle';

// Written by the banner that used to ask. Nothing writes it now.
const CONSENT_KEY = 'cj_analytics_consent';
// Opened only by the server's headless browser, with a token in the path.
const PRINT_ROUTE_RE = /^\/print\//;
let initialized = false;
// Set only once the real SDK has loaded and been handed a key/host. Distinct
// from `initialized`, which also covers test mode — without this split, `emit`
// called `posthog.capture` on a never-`init`'d instance during every
// scripts/flows.mjs run (posthog-js warns to the console and its behavior
// pre-init isn't a contract we want to depend on).
let sdk: PostHog | null = null;
// Calls made after `init` while the SDK is still loading; null once it has
// loaded, or failed to (a failed load drops them, as an unconfigured build does).
let pending: Array<(posthog: PostHog) => void> | null = null;

function storage(): Storage | null {
  try {
    return window.localStorage;
  } catch {
    return null;
  }
}

function optedOut(): boolean {
  // Privacy Policy 6.2: a Global Privacy Control signal keeps analytics off.
  if ((navigator as Navigator & { globalPrivacyControl?: boolean }).globalPrivacyControl === true) return true;
  // The print routes are our own render of a résumé, not a visit.
  if (PRINT_ROUTE_RE.test(window.location.pathname)) return true;
  // A Decline pressed on the old banner still stands in that browser.
  return storage()?.getItem(CONSENT_KEY) === 'declined';
}

function init(): void {
  if (initialized) return;
  if (window.__cjAnalyticsEvents) {
    // Test mode (scripts/flows.mjs pre-seeds this array before the app
    // loads): the sink is the whole point, so skip the real SDK and any
    // network noise it would make against an unconfigured or fake host.
    initialized = true;
    return;
  }
  const apiKey = import.meta.env.VITE_POSTHOG_KEY;
  const apiHost = import.meta.env.VITE_POSTHOG_HOST;
  if (!apiKey || !apiHost) return; // unconfigured (e.g. local dev) — stays a no-op, never throws
  initialized = true;
  pending = [];
  whenIdle(() => {
    import('posthog-js')
      .then(({ default: posthog }) => {
        start(posthog, apiKey, apiHost);
        sdk = posthog;
        const queued = pending ?? [];
        pending = null;
        for (const call of queued) call(posthog);
      })
      .catch(() => {
        pending = null;
      });
  });
}

function start(posthog: PostHog, apiKey: string, apiHost: string): void {
  posthog.init(apiKey, {
    api_host: apiHost,
    // Pageviews are driven off the router (see App.tsx), not native loads.
    capture_pageview: false,
    capture_exceptions: true,
    // Privacy Policy 6.3 says both are off. An autocaptured click carries the
    // text of the element, which on a résumé page can be résumé text.
    autocapture: false,
    disable_session_recording: true,
    // The project's remote settings (PostHog dashboard) can switch on more
    // automatic capture than `autocapture: false` stops: dead clicks are
    // automatic click capture (6.3), and surveys, heatmaps and performance
    // timing are not in the policy at all. Off here so a dashboard toggle can
    // never widen what the browser collects. Pinned by
    // scripts/check-analytics-privacy.mjs.
    capture_dead_clicks: false,
    capture_heatmaps: false,
    capture_performance: false,
    disable_surveys: true,
  });
}

/** Runs `call` now if the SDK is up, or queues it while it loads. */
function withSdk(call: (posthog: PostHog) => void): void {
  if (sdk) call(sdk);
  else pending?.push(call);
}

/** Called from `App.tsx`'s `AnalyticsPageview` at the top of its
 * pathname-keyed effect, before that effect sends anything — so init and
 * the first pageview/route-derived event always happen in the same effect
 * run, with no cross-component ordering to get wrong. (An earlier version
 * of this split `init` across two components; on a fresh load React fires
 * a child's mount effect before its parent's, so whichever one didn't own
 * the init call would run against `initialized === false` — silently
 * dropping whatever it tried to send, not just the pageview.) */
export function initAnalytics(): void {
  if (!optedOut()) init();
}

declare global {
  interface Window {
    // Set by `scripts/flows.mjs` before the app loads (`page.addInitScript`).
    // Undefined in production, where every push below is a no-op — the test
    // seam `flows.mjs` reads instead of parsing PostHog's own wire format,
    // which the flow script has no business knowing.
    __cjAnalyticsEvents?: Array<{ event: string; properties?: Record<string, unknown> }>;
  }
}

/** Records to the test sink (if present) only on the path that would
 * actually reach PostHog — so a flow run that opted out sees nothing, the
 * same as a real visitor whose browser sends Global Privacy Control. */
function emit(event: string, properties?: Record<string, unknown>): void {
  if (!initialized) return;
  window.__cjAnalyticsEvents?.push({ event, properties });
  // The time of the call, not of the send: a call queued while the SDK loads keeps its own.
  const timestamp = new Date();
  withSdk((posthog) => posthog.capture(event, properties, { timestamp }));
}

export function track(event: string, properties?: Record<string, unknown>): void {
  emit(event, properties);
}

export function identify(accountId: string): void {
  if (!initialized) return;
  window.__cjAnalyticsEvents?.push({ event: '$identify', properties: { accountId } });
  withSdk((posthog) => posthog.identify(accountId));
}

export function pageview(path: string): void {
  emit('$pageview', { $current_url: path });
}
